import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { SectionHeader, Grad } from '@/components/ui/Section';

interface Advantage {
  id: string;
  number: string;
  title: string;
  description: string;
  comparison: {
    traditional: string;
    graphene: string;
    improvement: string;
  };
  icon: string;
  image: string;
}

const TechnicalAdvantages = () => {
  const advantages: Advantage[] = [
    {
      id: 'adv_molecular',
      number: '01',
      title: 'Molecular-Level Enhancement',
      description:
        'Graphene integrates at the molecular level, creating stronger bonds between material matrices unlike macro-scale additives that simply fill voids.',
      comparison: {
        traditional: 'Surface-level mixing',
        graphene: 'Molecular integration',
        improvement: '+300% bonding efficiency',
      },
      icon: 'CubeTransparentIcon',
      image: '/assets/images/01.png',
    },
    {
      id: 'adv_surface',
      number: '02',
      title: 'Extreme Surface Area',
      description:
        'With 500-700 m²/g surface area, graphene provides maximum contact points for load distribution and stress transfer throughout the material.',
      comparison: {
        traditional: '10-50 m²/g (carbon black)',
        graphene: '500-700 m²/g',
        improvement: '+1000% active surface',
      },
      icon: 'Square3Stack3DIcon',
      image: '/assets/images/02.png',
    },
    {
      id: 'adv_thermal',
      number: '03',
      title: 'Superior Thermal Management',
      description:
        "Graphene's 5000 W/mK thermal conductivity enables rapid heat dissipation, preventing hotspots and thermal degradation in high-performance applications.",
      comparison: {
        traditional: '0.2-0.8 W/mK (polymers)',
        graphene: '5000 W/mK',
        improvement: '+6000x heat transfer',
      },
      icon: 'FireIcon',
      image: '/assets/images/03.png',
    },
    {
      id: 'adv_mechanical',
      number: '04',
      title: 'Unprecedented Strength',
      description:
        'With 130 GPa tensile strength and 1 TPa elastic modulus, graphene reinforces materials while maintaining flexibility and reducing weight.',
      comparison: {
        traditional: '3-5 GPa (steel)',
        graphene: '130 GPa',
        improvement: '+2500% tensile strength',
      },
      icon: 'ShieldCheckIcon',
      image: '/assets/images/04.png',
    },
  ];

  return (
    <section className="section relative overflow-hidden bg-ink text-white">
      {/* Supplied background artwork under a navy overlay for readability. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage: "url('/assets/images/Technical-Advantages-bg.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div aria-hidden="true" className="absolute inset-0 bg-ink/60 sm:bg-ink/50" />

      <div className="container-page relative">
        {/* Header — existing copy, standardised presentation (left-aligned in
            this split layout). */}
        <SectionHeader
          align="left"
          title={
            <>
              Technical <Grad>Advantages</Grad>
            </>
          }
          eyebrow="Why Graphene"
          lead="How graphene nanomaterials outperform traditional additives at the molecular level"
        />

        {/* 2x2 feature cards */}
        <div
          data-reveal-stagger=""
          className="mt-11 grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-6"
        >
          {advantages.map((advantage) => (
            <article
              key={advantage.id}
              className="group flex min-w-0 flex-col overflow-hidden rounded-xl border border-white/10 bg-[#0a1322]/85 shadow-[0_20px_50px_rgba(2,8,23,0.45)] transition-[border-color,box-shadow] duration-base ease-out hover:border-accent/45 hover:shadow-[0_24px_60px_rgba(2,8,23,0.55),0_0_28px_rgba(14,165,233,0.12)]"
            >
              <div className="grid flex-1 grid-cols-1 sm:grid-cols-[minmax(0,1fr)_38%]">
                {/* Content */}
                <div className="min-w-0 p-6 sm:p-7">
                  <div className="flex items-center gap-3">
                    <span className="bg-accent-rich t-value flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-sm font-bold text-white shadow-[0_4px_14px_rgba(14,165,233,0.3)]">
                      {advantage.number}
                    </span>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-accent/40 bg-accent/10 text-accent">
                      <Icon name={advantage.icon as any} size={19} />
                    </span>
                  </div>

                  <h3 className="mt-4 text-lg font-semibold leading-snug text-white">
                    {advantage.title}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-300">
                    {advantage.description}
                  </p>
                </div>

                {/* Thumbnail — top strip on mobile, right column from sm. */}
                <div className="relative order-first h-36 min-w-0 sm:order-last sm:h-auto sm:min-h-[176px]">
                  <AppImage
                    src={advantage.image}
                    alt=""
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 38vw, 19vw"
                    className="img-zoom h-full w-full object-cover"
                  />
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-gradient-to-t from-[#0a1322] via-[#0a1322]/20 to-transparent sm:bg-gradient-to-r sm:from-[#0a1322] sm:via-[#0a1322]/15 sm:to-transparent"
                  />
                </div>
              </div>

              {/* Comparison row — existing values, unchanged. */}
              <div className="border-t border-white/10 bg-[#081120]/70 px-6 py-4 sm:px-7">
                <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="t-value text-sm text-slate-400">
                    {advantage.comparison.traditional}
                  </span>
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                    className="shrink-0 text-accent"
                  >
                    <path
                      d="M3 10h13M11 4.5 16.5 10 11 15.5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="t-value text-sm font-semibold text-sky-400">
                    {advantage.comparison.graphene}
                  </span>
                </p>
                <p className="t-value mt-1.5 text-[0.8125rem] font-semibold text-emerald-400">
                  {advantage.comparison.improvement}
                </p>
              </div>
            </article>
          ))}
        </div>

        {/* Bottom CTA — existing copy, restyled onto the dark ladder. */}
        <div className="mt-12 rounded-xl border border-accent/30 bg-[#0a1322]/85 p-8 text-center shadow-[0_20px_50px_rgba(2,8,23,0.45)] sm:p-12">
          <h3 className="t-h3 text-white">See the Difference in Your Application</h3>
          <p className="mx-auto mt-3 max-w-2xl text-[0.9375rem] leading-relaxed text-slate-300">
            Request a comparative analysis to see how graphene can enhance your specific materials
          </p>
          <Link
            href="/contact"
            className="mt-7 inline-flex min-h-[50px] items-center justify-center rounded-md bg-accent px-8 text-[0.8125rem] font-semibold uppercase tracking-[0.12em] text-white shadow-[0_2px_14px_rgba(14,165,233,0.35),inset_0_1px_0_rgba(255,255,255,0.2)] transition-[background-color,box-shadow,transform] duration-fast ease-out hover:bg-sky-400 hover:shadow-[0_5px_20px_rgba(14,165,233,0.45)] active:translate-y-px motion-reduce:active:transform-none"
          >
            Request Analysis
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TechnicalAdvantages;
